import { useEffect, useRef, useState } from 'react';
import cx from 'clsx';
import {
  ActionIcon,
  Box,
  Button,
  Group,
  Loader,
  Menu,
  Modal,
  MultiSelect,
  Stack,
  Text,
  TextInput,
  UnstyledButton,
} from '@mantine/core';
import { isNotEmpty, useForm } from '@mantine/form';
import { RichTextEditor } from '@mantine/tiptap';
import type { Editor } from '@tiptap/core';
import Link from '@tiptap/extension-link';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { IconDotsVertical } from '@tabler/icons-react';
import { SaveErrorBox } from '@/components/SaveErrorBox/SaveErrorBox';
import { embedEmailEditorDesign, extractEmailEditorDesign } from '@/helpers';
import { useSaveMessageTemplateDraftMutation, useUpdateMessageTemplateMutation } from '@/hooks/message-templates';
import type { TagDTO, TemplateDTO } from '@/types/messageTemplates';
import type { EmailEditorDesign, EmailEditorExportData } from '@/types/emailEditorDesign';
import type { AxiosError } from 'axios';
import type { ErrorResponse } from '@/types/error';
import { MessageDesigner } from '../MessageDesigner/MessageDesigner';
import { MessageEmailPreview } from '../MessageEmailPreview/MessageEmailPreview';
import styles from './CreateTemplateModal.module.sass';

type TemplateFormData = {
  title: string;
  content: string;
  tagIds: string[];
};

type CreateTemplateModalProps = {
  opened: boolean;
  onClose: () => void;
  onCreated: (template: TemplateDTO) => void;
  onUpdated?: (template: TemplateDTO) => void;
  availableTags: TagDTO[];
  editTemplate?: TemplateDTO | null;
  startInDesigner?: boolean;
};

const stripHtml = (html: string): string => {
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent ?? '';
};

/** TipTap can leave a non-null `Editor` reference after destroy; `.commands` then throws. */
const setEditorHtmlIfAlive = (editor: Editor | null, html: string): void => {
  if (!editor || editor.isDestroyed) {
    return;
  }
  editor.commands.setContent(html, { emitUpdate: false });
};

export const CreateTemplateModal = ({
  opened,
  onClose,
  onCreated,
  onUpdated,
  availableTags,
  editTemplate,
  startInDesigner = false,
}: CreateTemplateModalProps) => {
  const isEditMode = !!editTemplate;
  const saveDraftMutation = useSaveMessageTemplateDraftMutation();
  const updateTemplateMutation = useUpdateMessageTemplateMutation();
  const [currentView, setCurrentView] = useState<'form' | 'designer'>(startInDesigner ? 'designer' : 'form');
  const [hasOpenedDesigner, setHasOpenedDesigner] = useState(startInDesigner);
  const [editorDesign, setEditorDesign] = useState<EmailEditorDesign | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<AxiosError<ErrorResponse> | null>(null);
  const editorDesignRef = useRef<EmailEditorDesign | null>(null);
  const skipHydrateFromTemplateRef = useRef(false);
  editorDesignRef.current = editorDesign;

  const initialValues: TemplateFormData = editTemplate
    ? {
        title: editTemplate.title,
        content: editTemplate.content,
        tagIds: editTemplate.tags.map((t) => t.id),
      }
    : { title: '', content: '', tagIds: [] };

  const form = useForm<TemplateFormData>({
    initialValues,
    validate: {
      title: isNotEmpty('Title is required'),
      content: (value) => (!stripHtml(value || '').trim() ? 'Content is required' : null),
    },
  });

  const tagSelectData = availableTags.map((tag) => ({ value: tag.id, label: tag.name }));

  const editor = useEditor(
    {
      immediatelyRender: true,
      extensions: [StarterKit.configure({ link: false }), Link],
      content: initialValues.content,
      onUpdate: ({ editor: e }) => {
        if (editorDesignRef.current) {
          return;
        }
        form.setFieldValue('content', e.getHTML());
      },
    },
    [opened, editTemplate?.id]
  );

  useEffect(() => {
    if (!opened) {
      skipHydrateFromTemplateRef.current = false;
      return;
    }

    if (skipHydrateFromTemplateRef.current) {
      return;
    }

    if (editTemplate) {
      const { html, design } = extractEmailEditorDesign({ html: editTemplate.content });
      const nextValues: TemplateFormData = {
        title: editTemplate.title,
        content: editTemplate.content,
        tagIds: editTemplate.tags.map((t) => t.id),
      };
      form.setValues(nextValues);
      setEditorHtmlIfAlive(editor, html);
      setEditorDesign(design);
    } else {
      const emptyValues: TemplateFormData = { title: '', content: '', tagIds: [] };
      form.setValues(emptyValues);
      setEditorHtmlIfAlive(editor, '');
      setEditorDesign(null);
    }
  }, [opened, editTemplate?.id, editor]);

  const resetState = () => {
    skipHydrateFromTemplateRef.current = false;
    form.reset();
    setEditorDesign(null);
    setHasOpenedDesigner(false);
    setCurrentView('form');
    setSaveError(null);
    setIsSaving(false);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleGoToDesigner = () => {
    if (!editorDesign) {
      const { design } = extractEmailEditorDesign({ html: form.values.content });
      if (design) {
        setEditorDesign(design);
      }
    }
    setHasOpenedDesigner(true);
    setCurrentView('designer');
  };

  const handleReturnFromDesigner = (data: EmailEditorExportData) => {
    const nextDesign = data.design ? (JSON.parse(JSON.stringify(data.design)) as EmailEditorDesign) : null;
    skipHydrateFromTemplateRef.current = true;
    setEditorDesign(nextDesign);
    form.setFieldValue('content', embedEmailEditorDesign({ html: data.html, design: nextDesign }));
    setCurrentView('form');
  };

  const handleCancelDesigner = () => {
    if (startInDesigner) {
      handleClose();
      return;
    }
    setCurrentView('form');
  };

  const handleDeleteDesign = () => {
    skipHydrateFromTemplateRef.current = true;
    setEditorDesign(null);
    setHasOpenedDesigner(false);
    form.setFieldValue('content', '');
    setEditorHtmlIfAlive(editor, '');
  };

  const handleSubmit = async (values: TemplateFormData) => {
    setIsSaving(true);
    setSaveError(null);
    try {
      const selectedTags = values.tagIds.reduce<TemplateDTO['tags']>((acc, id) => {
        const tag = availableTags.find((t) => t.id === id);
        if (tag) {
          acc.push({ id: tag.id, name: tag.name });
        }
        return acc;
      }, []);

      if (isEditMode) {
        const currentTagIds = new Set(editTemplate.tags.map((t) => t.id));
        const selectedTagIds = new Set(values.tagIds);
        const removedTags = editTemplate.tags
          .filter((t) => !selectedTagIds.has(t.id))
          .map((t) => ({ id: t.id, name: t.name, isRemove: true }));
        const addedTags = values.tagIds
          .filter((id) => !currentTagIds.has(id))
          .map((id) => {
            const tag = availableTags.find((t) => t.id === id);
            return tag ? { id: tag.id, name: tag.name, isRemove: false } : null;
          })
          .filter(Boolean) as { id: string; name: string; isRemove: boolean }[];
        const tagsToUpdate = [...removedTags, ...addedTags];
        await updateTemplateMutation.mutateAsync({
          id: editTemplate.id,
          payload: {
            title: values.title,
            content: values.content,
            status: editTemplate.status as 'draft' | 'published',
            ...(tagsToUpdate.length ? { tags: tagsToUpdate } : {}),
          },
        });
        const updated: TemplateDTO = {
          ...editTemplate,
          title: values.title,
          content: values.content,
          tags: selectedTags,
          updatedDate: new Date().toISOString(),
        };
        onUpdated?.(updated);
      } else {
        const payload: TemplateDTO = {
          id: '',
          title: values.title,
          status: 'draft',
          content: values.content,
          createdDate: new Date().toISOString(),
          updatedDate: new Date().toISOString(),
          tags: selectedTags,
        };
        const created = await saveDraftMutation.mutateAsync(payload);
        onCreated(created);
      }
      handleClose();
    } catch (error) {
      setSaveError(error as AxiosError<ErrorResponse>);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      {opened && hasOpenedDesigner && (
        <div className={cx(styles.designerOverlay, currentView !== 'designer' && styles.designerOverlayHidden)}>
          <MessageDesigner
            key={editTemplate?.id ?? 'create'}
            editorDesign={editorDesign}
            messageText={form.values.content}
            onSave={handleReturnFromDesigner}
            onCancel={handleCancelDesigner}
          />
        </div>
      )}
    <Modal
      opened={opened && currentView === 'form'}
      onClose={handleClose}
      title={isEditMode ? 'Edit Template' : 'Create Template'}
      size="lg"
      classNames={{
        header: styles.modalHeader,
        title: styles.modalTitle,
        close: styles.closeButton,
      }}
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md" mt="md">
          <TextInput {...form.getInputProps('title')} label="Template Title" required />

          <MultiSelect
            label="Tags"
            placeholder="Select tags"
            data={tagSelectData}
            {...form.getInputProps('tagIds')}
            clearable
            searchable
          />

          {opened && !isEditMode && !editorDesign && (
            <Box>
              <Group justify="space-between" gap="xs" align="center" w="100%" mb="xs">
                <Text component="span" className={styles.designerLabel}>
                  Content
                  <span className={styles.asterisk}> *</span>
                </Text>
                <UnstyledButton onClick={handleGoToDesigner} c="blue" size="sm" type="button">
                  Message Designer
                </UnstyledButton>
              </Group>
              {editor && !editor.isDestroyed ? (
                <RichTextEditor editor={editor}>
                  <RichTextEditor.Toolbar>
                    <RichTextEditor.ControlsGroup>
                      <RichTextEditor.Bold />
                      <RichTextEditor.Italic />
                      <RichTextEditor.Underline />
                      <RichTextEditor.Strikethrough />
                      <RichTextEditor.ClearFormatting />
                    </RichTextEditor.ControlsGroup>
                    <RichTextEditor.ControlsGroup>
                      <RichTextEditor.H1 />
                      <RichTextEditor.H2 />
                      <RichTextEditor.H3 />
                      <RichTextEditor.H4 />
                    </RichTextEditor.ControlsGroup>
                    <RichTextEditor.ControlsGroup>
                      <RichTextEditor.BulletList />
                      <RichTextEditor.OrderedList />
                      <RichTextEditor.Link />
                      <RichTextEditor.Unlink />
                    </RichTextEditor.ControlsGroup>
                    <RichTextEditor.ControlsGroup>
                      <RichTextEditor.Blockquote />
                      <RichTextEditor.Hr />
                    </RichTextEditor.ControlsGroup>
                    <RichTextEditor.ControlsGroup>
                      <RichTextEditor.Undo />
                      <RichTextEditor.Redo />
                    </RichTextEditor.ControlsGroup>
                  </RichTextEditor.Toolbar>
                  <RichTextEditor.Content />
                </RichTextEditor>
              ) : (
                <Loader size="sm" type="dots" />
              )}
              {form.errors.content && (
                <Text size="sm" c="red" mt="xs">
                  {form.errors.content}
                </Text>
              )}
            </Box>
          )}

          {(isEditMode || editorDesign) && (
            <Box>
              <Group justify="space-between" gap="xs" align="center" w="100%" mb="xs">
                <Text component="span" className={styles.designerLabel}>
                  Content
                  <span className={styles.asterisk}> *</span>
                </Text>
                <div className={styles.previewActions}>
                  <UnstyledButton onClick={handleGoToDesigner} c="blue" size="sm" type="button">
                    Edit in Designer
                  </UnstyledButton>
                  {editorDesign && (
                    <Menu shadow="md" width={200}>
                      <Menu.Target>
                        <ActionIcon size="sm" variant="transparent" color="gray.6" type="button">
                          <IconDotsVertical size={20} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item color="red" onClick={handleDeleteDesign}>
                          Delete design
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  )}
                </div>
              </Group>
              <div className={styles.editPreviewArea}>
                <MessageEmailPreview content={form.values.content} className={styles.editPreview} />
              </div>
              {form.errors.content && (
                <Text size="sm" c="red" mt="xs">
                  {form.errors.content}
                </Text>
              )}
            </Box>
          )}

          <SaveErrorBox error={saveError} />

          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" loading={isSaving}>
              {isEditMode ? 'Save Changes' : 'Save as Draft'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
    </>
  );
};
